import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, DollarSign, AlertTriangle, Clock, CheckCircle2, Send } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";

const STATUSES = ["New", "Acknowledged", "Assigned", "In Progress", "Waiting for Guest", "Completed", "Cancelled", "Declined"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

function ConciergeCard({ c, onUpdate }) {
  const [reply, setReply] = useState("");
  const [note, setNote] = useState(c.internal_note || "");
  const [assignee, setAssignee] = useState(c.assignee || "");
  const [charge, setCharge] = useState({ amount: "", label: "" });

  const upd = (body) => onUpdate(c.id, body);
  const mins = (m) => (m == null ? "—" : m < 60 ? `${m}m` : `${(m / 60).toFixed(1)}h`);

  return (
    <Panel title={<span className="flex items-center gap-2">{c.category} <StatusBadge status={c.status} /></span>}>
      <p className="text-sm text-muted-foreground mb-1">{c.guest_name} · <span className="capitalize">{c.priority}</span></p>
      <p className="text-sm mb-2">{c.notes}</p>
      {c.desired_time && <p className="text-xs text-muted-foreground">Desired: {c.desired_time}</p>}

      <div className="flex gap-4 text-[11px] text-muted-foreground mt-2">
        <span className="flex items-center gap-1"><Clock size={11} /> Response: {mins(c.response_minutes)}</span>
        <span className="flex items-center gap-1"><CheckCircle2 size={11} /> Completion: {mins(c.completion_minutes)}</span>
        {c.incident_id && <span className="flex items-center gap-1 text-red-300"><AlertTriangle size={11} /> Incident filed</span>}
      </div>

      {c.messages?.map((m) => (
        <p key={m.id} className={`text-xs mt-2 border-l-2 pl-2 ${m.from === "host" ? "border-pool/40 text-pool" : "border-white/20 text-muted-foreground"}`}>{m.from}: {m.text}</p>
      ))}

      <div className="grid grid-cols-2 gap-2 mt-3">
        <select className={adminInput} value={c.status} onChange={(e) => upd({ status: e.target.value })} data-testid={`concierge-status-${c.id}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select className={adminInput} value={c.priority || "normal"} onChange={(e) => upd({ priority: e.target.value })} data-testid={`concierge-priority-${c.id}`}>{PRIORITIES.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}</select>
      </div>

      <div className="flex gap-2 mt-2">
        <input className={adminInput} placeholder="Assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} />
        <button onClick={() => upd({ assignee })} className="btn-ghost-gold px-3 text-xs whitespace-nowrap">Assign</button>
      </div>

      <div className="flex gap-2 mt-2">
        <input className={adminInput} placeholder="Internal note (staff only)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button onClick={() => upd({ internal_note: note })} className="btn-ghost-gold px-3 text-xs whitespace-nowrap">Save</button>
      </div>

      <div className="flex gap-2 mt-2">
        <input className={adminInput} placeholder="Reply to guest…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`concierge-reply-${c.id}`} />
        <button onClick={() => { if (reply) { upd({ guest_message: reply }); setReply(""); } }} className="btn-ghost-gold px-3 text-xs flex items-center gap-1 whitespace-nowrap"><Send size={12} /> Send</button>
      </div>

      {c.reservation_id && (
        <div className="flex gap-2 mt-2">
          <input className={adminInput + " w-24"} type="number" placeholder="$" value={charge.amount} onChange={(e) => setCharge((p) => ({ ...p, amount: e.target.value }))} data-testid={`concierge-charge-amt-${c.id}`} />
          <input className={adminInput} placeholder="Charge label" value={charge.label} onChange={(e) => setCharge((p) => ({ ...p, label: e.target.value }))} />
          <button onClick={() => { if (charge.amount) { upd({ charge_amount: parseFloat(charge.amount), charge_label: charge.label }); setCharge({ amount: "", label: "" }); } }} className="btn-ghost-gold px-3 text-xs flex items-center gap-1 whitespace-nowrap" data-testid={`concierge-charge-${c.id}`}><DollarSign size={12} /> Folio</button>
        </div>
      )}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1" data-testid={`concierge-rating-${c.id}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => upd({ resolution_rating: n })} title={`Rate ${n}`}>
              <Star size={15} className={n <= (c.resolution_rating || 0) ? "text-gold fill-gold" : "text-muted-foreground"} />
            </button>
          ))}
        </div>
        {!c.incident_id && (
          <button onClick={() => upd({ convert_to_incident: true })} className="text-xs text-red-300 hover:text-red-400 flex items-center gap-1" data-testid={`concierge-incident-${c.id}`}><AlertTriangle size={12} /> Convert to incident</button>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground mt-2">{fmtDate(c.created_at)}</p>
    </Panel>
  );
}

export default function AdminConcierge() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/concierge").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const update = async (id, body) => { try { await api.put(`/admin/concierge/${id}`, body); toast.success("Updated."); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <AdminHeading title="Concierge Requests" subtitle={`${items?.length ?? 0} total · assign, prioritize, charge to folio, track response & completion time`} />
      {items === null ? <Loading /> : items.length === 0 ? <Panel><p className="text-sm text-muted-foreground py-4 text-center">No concierge requests yet.</p></Panel> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((c) => <ConciergeCard key={c.id} c={c} onUpdate={update} />)}
        </div>
      )}
    </div>
  );
}
